import { isNumber } from 'lodash';
import { checkIfPaidToPaidFlow } from '@/views/checkout/payment/helper/util';
import { flowTypes } from '@/common/service/page-config';
import { INR_DEFAULT_TAX_RATE } from '@/views/checkout/constant';
import { logger } from '@/utils/log';

/**
 * Get INR tax rate from basic config
 * @param {Object} basicConfig - Basic config from store
 * @returns {number} Tax rate multiplier
 */
export const getInrTaxRate = (basicConfig) => {
  return 1 + (isNumber(basicConfig?.indiaFixedTaxRate) ? basicConfig?.indiaFixedTaxRate : INR_DEFAULT_TAX_RATE);
};

/**
 * Calculate monthly amount with tax
 * @param {Object} nextBillAmount - Next bill amount from store
 * @param {number} inrTaxRate - Tax rate multiplier
 * @returns {number} Monthly amount
 */
export const getMonthlyAmount = (nextBillAmount, inrTaxRate) => {
  const monthlyCharge = isNumber(nextBillAmount?.monthlyCharge) ? nextBillAmount?.monthlyCharge : 0;
  const monthlyDiscount = isNumber(nextBillAmount?.monthlyDiscount) ? nextBillAmount?.monthlyDiscount : 0;
  return (monthlyCharge + monthlyDiscount) * inrTaxRate;
};

/**
 * Calculate annual term monthly amount with tax
 * @param {Object} nextBillAmount - Next bill amount from store
 * @param {number} inrTaxRate - Tax rate multiplier
 * @returns {number} Annual term monthly amount
 */
export const getAnnualTermMonthlyAmount = (nextBillAmount, inrTaxRate) => {
  const annualTermMonthlyCharge = isNumber(nextBillAmount?.annualTermMonthlyCharge) ? nextBillAmount?.annualTermMonthlyCharge : 0;
  const annualTermMonthlyDiscount = isNumber(nextBillAmount?.annualTermMonthlyDiscount) ? nextBillAmount?.annualTermMonthlyDiscount : 0;
  return (annualTermMonthlyCharge + annualTermMonthlyDiscount) * inrTaxRate;
};

/**
 * Calculate annually amount with tax
 * @param {Object} nextBillAmount - Next bill amount from store
 * @param {number} inrTaxRate - Tax rate multiplier
 * @returns {number} Annually amount
 */
export const getAnnuallyAmount = (nextBillAmount, inrTaxRate) => {
  const annualCharge = isNumber(nextBillAmount?.annualCharge) ? nextBillAmount?.annualCharge : 0;
  const annualDiscount = isNumber(nextBillAmount?.annualDiscount) ? nextBillAmount?.annualDiscount : 0;
  return (annualCharge + annualDiscount) * inrTaxRate;
};

/**
 * Calculate quarterly amount with tax
 * @param {Object} nextBillAmount - Next bill amount from store
 * @param {number} inrTaxRate - Tax rate multiplier
 * @returns {number} Quarterly amount
 */
export const getQuarterlyAmount = (nextBillAmount, inrTaxRate) => {
  const quarterlyCharge = isNumber(nextBillAmount?.quarterlyCharge) ? nextBillAmount?.quarterlyCharge : 0;
  const quarterlyDiscount = isNumber(nextBillAmount?.quarterlyDiscount) ? nextBillAmount?.quarterlyDiscount : 0;
  return (quarterlyCharge + quarterlyDiscount) * inrTaxRate;
};

/**
 * Calculate two years amount with tax
 * @param {Object} nextBillAmount - Next bill amount from store
 * @param {number} inrTaxRate - Tax rate multiplier
 * @returns {number} Two years amount
 */
export const getTwoYearsAmount = (nextBillAmount, inrTaxRate) => {
  const twoYearsCharge = isNumber(nextBillAmount?.twoYearsCharge) ? nextBillAmount?.twoYearsCharge : 0;
  const twoYearsDiscount = isNumber(nextBillAmount?.twoYearsDiscount) ? nextBillAmount?.twoYearsDiscount : 0;
  return (twoYearsCharge + twoYearsDiscount) * inrTaxRate;
};

/**
 * Calculate three years amount with tax
 * @param {Object} nextBillAmount - Next bill amount from store
 * @param {number} inrTaxRate - Tax rate multiplier
 * @returns {number} Three years amount
 */
export const getThreeYearsAmount = (nextBillAmount, inrTaxRate) => {
  const threeYearsCharge = isNumber(nextBillAmount?.threeYearsCharge) ? nextBillAmount?.threeYearsCharge : 0;
  const threeYearsDiscount = isNumber(nextBillAmount?.threeYearsDiscount) ? nextBillAmount?.threeYearsDiscount : 0;
  return (threeYearsCharge + threeYearsDiscount) * inrTaxRate;
};

/**
 * The amount of recurring charges of the current plan
 * @param {Object} params - Parameters object
 * @param {Array} params.selectedPlans - Selected plans in cart
 * @param {Array} params.boughtPlans - Currently owned plans
 * @param {number} params.inrTaxRate - Tax rate multiplier
 * @returns {number} Subtract amount
 */
export const getSubtractAmount = ({ selectedPlans, boughtPlans, inrTaxRate }) => {
  let subtractAmount = 0;

  // make sure it's a P2P flow
  if (!checkIfPaidToPaidFlow(window.$context.flow)) {
    return subtractAmount;
  }

  let selectedPlansList = selectedPlans ?? [];
  let boughtPlansList = boughtPlans ?? [];

  // In add2cart flow, user is ADDING a new subscription (MSQ scenario)
  // In edit flow, user is MODIFYING/REPLACING an existing subscription
  const isAddingNewSubscription = window.$context.flow === flowTypes.add2cart;

  try {
    // find plans to subtract
    const plansToSubtract = selectedPlansList
      .map(selectedPlan => {
        if (isAddingNewSubscription) {
          // ADD flow (MSQ): Only subtract if EXACT match (same type AND same billing cycle)
          // Adding Monthly alongside Annual = different cycle = NO subtract
          return boughtPlansList.find(boughtPlan =>
            boughtPlan.type === selectedPlan.type &&
            boughtPlan.billing_cycle === selectedPlan.billing_cycle
          );
        } else {
          // EDIT flow: Subtract plan of same type (user is replacing existing subscription)
          return boughtPlansList.find(boughtPlan =>
            boughtPlan.type === selectedPlan.type
          );
        }
      })
      .filter(existingPlan => existingPlan);

    // calculate total amount to subtract
    subtractAmount = plansToSubtract.reduce((total, plan) => {
      const existingLicenseCount = plan?.cartInfo?.selected_number || 1;
      const amountPerLicense = plan?.amount || 0;
      return total + (existingLicenseCount * amountPerLicense * inrTaxRate) / 100;
    }, 0);

  } catch (error) {
    logger.error(error);
  }

  return subtractAmount;
};

/**
 * Calculate mandate amount for P2P flow
 * @param {Object} params - Parameters object
 * @returns {number} Mandate amount for P2P flow
 */
export const calculateMandateAmountForPaidToPaidFlow = ({
  basicConfig,
  isSelectedSameAsEditablePlansInBought,
  nextBillAmount,
  inrTaxRate,
  selectedPlans,
  boughtPlans,
  indiaMandateFactor,
  indiaMinMandateAmount,
  indiaMaxMandateAmount,
  getTotalCharge,
  getTaxTotalAmount,
}) => {
  // current subscription amount
  const currentSubscriptionAmount = isNumber(basicConfig?.totalSubscriptionAmount) ? basicConfig?.totalSubscriptionAmount : 0;

  // current subscription is the same as in the cart
  if (isSelectedSameAsEditablePlansInBought) {
    return currentSubscriptionAmount;
  }

  // new subscription amount
  const annuallyAmount = getAnnuallyAmount(nextBillAmount, inrTaxRate);
  const monthlyAmount = getMonthlyAmount(nextBillAmount, inrTaxRate);
  const quarterlyAmount = getQuarterlyAmount(nextBillAmount, inrTaxRate);
  const twoYearsAmount = getTwoYearsAmount(nextBillAmount, inrTaxRate);
  const threeYearsAmount = getThreeYearsAmount(nextBillAmount, inrTaxRate);
  const annualTermMonthlyAmount = getAnnualTermMonthlyAmount(nextBillAmount, inrTaxRate);
  const newSubscriptionAmount = (annuallyAmount + monthlyAmount + quarterlyAmount + twoYearsAmount + threeYearsAmount + annualTermMonthlyAmount) / 100;

  // new recurring amount
  const subtractAmount = getSubtractAmount({ selectedPlans, boughtPlans, inrTaxRate });
  const newRecurringAmount = currentSubscriptionAmount - subtractAmount + newSubscriptionAmount;

  // calculate mandate amount
  const calculatedMandateAmount = Math.max(newRecurringAmount * indiaMandateFactor, indiaMinMandateAmount);
  const totalChargeToday = (getTotalCharge + getTaxTotalAmount) / 100;
  const mandateAmount = Math.min(Math.max(calculatedMandateAmount, totalChargeToday), indiaMaxMandateAmount);
  return Math.ceil(mandateAmount); // avoid Zuora error by rounding up the value to the nearest integer
};

/**
 * Calculate UPI mandate amount
 * @param {Object} getters - Vuex getters object
 * @returns {number} Calculated mandate amount
 */
export const calculateUpiMandateAmount = (getters) => {
  const {
    basicConfig,
    nextBillAmount,
    indiaMandateFactor,
    indiaMinMandateAmount,
    indiaMaxMandateAmount,
    isSelectedSameAsEditablePlansInBought,
    getSelectedPlans,
    boughtPlans,
    getTotalCharge,
    getTaxTotalAmount,
  } = getters;

  const inrTaxRate = getInrTaxRate(basicConfig);

  if (checkIfPaidToPaidFlow(window.$context.flow)) {
    return calculateMandateAmountForPaidToPaidFlow({
      basicConfig,
      isSelectedSameAsEditablePlansInBought,
      nextBillAmount,
      inrTaxRate,
      selectedPlans: getSelectedPlans,
      boughtPlans,
      indiaMandateFactor,
      indiaMinMandateAmount,
      indiaMaxMandateAmount,
      getTotalCharge,
      getTaxTotalAmount,
    });
  }

  // new subscription amount
  const annuallyAmount = getAnnuallyAmount(nextBillAmount, inrTaxRate);
  const monthlyAmount = getMonthlyAmount(nextBillAmount, inrTaxRate);
  const quarterlyAmount = getQuarterlyAmount(nextBillAmount, inrTaxRate);
  const twoYearsAmount = getTwoYearsAmount(nextBillAmount, inrTaxRate);
  const threeYearsAmount = getThreeYearsAmount(nextBillAmount, inrTaxRate);
  const annualTermMonthlyAmount = getAnnualTermMonthlyAmount(nextBillAmount, inrTaxRate);
  const newSubscriptionAmount = (annuallyAmount + monthlyAmount + quarterlyAmount + twoYearsAmount + threeYearsAmount + annualTermMonthlyAmount) / 100;

  // calculate mandate amount
  const calculatedMandateAmount = Math.max(newSubscriptionAmount * indiaMandateFactor, indiaMinMandateAmount);
  const totalChargeToday = (getTotalCharge + getTaxTotalAmount) / 100;
  const mandateAmount = Math.min(Math.max(calculatedMandateAmount, totalChargeToday), indiaMaxMandateAmount);
  return Math.ceil(mandateAmount); // avoid Zuora error by rounding up the value to the nearest integer
};
